import { keepPreviousData, queryOptions, skipToken } from '@tanstack/react-query';

import {
    type ActivityLogFilters,
    type AppSessionFilters,
    type AppUsageParams,
    type BrandIntelligenceParams,
    type ClientHeatmapParams,
    type ClientsParams,
    type DashboardSalesParams,
    type LookbookAttentionParams,
    type OrderQueueParams,
    reportsApi,
} from '@/features/reports/api';
import { type ReportPeriod, type TaxPeriod } from '@/features/reports/periods';

export const reportsKeys = {
    appSessions: (filters: AppSessionFilters) => ['reports', 'app-sessions', filters] as const,
    appSessionPeople: (params: AppUsageParams) =>
        ['reports', 'app-sessions', 'people', params] as const,
    appSessionsWindow: (params: AppUsageParams) =>
        ['reports', 'app-sessions', 'window', params] as const,
    appUsage: (params: AppUsageParams) => ['reports', 'app-usage', params] as const,
    clientHeatmap: (params: ClientHeatmapParams) => ['reports', 'client-heatmap', params] as const,
    clientHeatmapLog: (filters: ActivityLogFilters) =>
        ['reports', 'client-heatmap', 'log', filters] as const,
    lookbookAttention: (params: LookbookAttentionParams) =>
        ['reports', 'client-heatmap', 'lookbook-pages', params] as const,
    overview: (period: ReportPeriod) => ['reports', 'overview', period] as const,
    dashboardSales: (params: DashboardSalesParams) =>
        ['reports', 'dashboard-sales', params] as const,
    transactions: (period: ReportPeriod) => ['reports', 'transactions', period] as const,
    orderQueue: (params: OrderQueueParams) =>
        ['reports', 'transactions', 'orders', params] as const,
    tax: (period: TaxPeriod) => ['reports', 'tax', period] as const,
    clients: (params: ClientsParams) => ['reports', 'clients', params] as const,
    brands: ['reports', 'brands'] as const,
    brandIntelligence: (params: BrandIntelligenceParams) =>
        ['reports', 'brand-intelligence', params] as const,
};

/**
 * `enabled` is how the page waits for the register to measure how many rows fit. The log is
 * 24,630 rows in production, so asking for a page before the size is known is a round trip whose
 * answer is thrown away the moment the measurement lands.
 */
export const appSessionsQuery = (filters: AppSessionFilters, enabled = true) =>
    queryOptions({
        queryKey: reportsKeys.appSessions(filters),
        queryFn: () => reportsApi.appSessions.page(filters),
        // The table holds still while the next page arrives, so paging does not blink through
        // an empty state and the row height stays put under the cursor.
        placeholderData: keepPreviousData,
        enabled,
    });

export const appSessionPeopleQuery = (params: AppUsageParams) =>
    queryOptions({
        queryKey: reportsKeys.appSessionPeople(params),
        queryFn: () => reportsApi.appSessions.people(params),
        // Nobody is added to this list while the page is open; it changes when someone new signs in.
        staleTime: 5 * 60 * 1000,
    });

/**
 * The whole Overview in one request (ALS-85): a report page loads and fails as one, never card by
 * card. The figures of the period before stay on screen while the next period arrives, so a
 * click on the period control does not blank the page.
 */
export const reportOverviewQuery = (period: ReportPeriod) =>
    queryOptions({
        queryKey: reportsKeys.overview(period),
        queryFn: () => reportsApi.overview(period),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });

/** The dashboard's Sales card, the same figures as the report through the same fold. */
export const dashboardSalesQuery = (params: DashboardSalesParams, enabled = true) =>
    queryOptions({
        queryKey: reportsKeys.dashboardSales(params),
        queryFn: () => reportsApi.dashboardSales(params),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
        enabled,
    });

/**
 * Transactions' Margin, Returns and Products for a period (ALS-86), in one request: the three
 * views load and fail as one. The period before stays on screen while the next one arrives.
 */
export const reportTransactionsQuery = (period: ReportPeriod) =>
    queryOptions({
        queryKey: reportsKeys.transactions(period),
        queryFn: () => reportsApi.transactions(period),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });

/** The order queue under its filters: a live list, so a short freshness. */
export const orderQueueQuery = (params: OrderQueueParams) =>
    queryOptions({
        queryKey: reportsKeys.orderQueue(params),
        queryFn: () => reportsApi.orderQueue(params),
        placeholderData: keepPreviousData,
        staleTime: 30 * 1000,
    });

/**
 * Tax reconciliation for a filing cycle (ALS-90). A read writes nothing (ADR 0095). Unlike the
 * other reports, the cycle before does not stay on screen while the next arrives: a cycle is a
 * different set of filing periods, so Month's rows under a chosen Year read as Year's figures
 * (ALS-476). The page shows its loading state instead.
 */
export const reportTaxQuery = (period: TaxPeriod) =>
    queryOptions({
        queryKey: reportsKeys.tax(period),
        queryFn: () => reportsApi.tax(period),
        staleTime: 60 * 1000,
    });

/**
 * Clients' five views for a period, a window and a manager (ALS-87), in one request: they load and
 * fail as one, and moving between them asks nothing again. The figures before stay on screen while
 * the next ones arrive.
 */
export const reportClientsQuery = (params: ClientsParams) =>
    queryOptions({
        queryKey: reportsKeys.clients(params),
        queryFn: () => reportsApi.clients(params),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });

/** Brand intelligence's controls (ALS-91): the brand register and the quarters the data covers. */
export const reportBrandsQuery = queryOptions({
    queryKey: reportsKeys.brands,
    queryFn: reportsApi.brands,
    staleTime: 5 * 60 * 1000,
});

/**
 * One brand's Demand and Commercial tiles for a quarter, in one request (ALS-91). Waits for a brand
 * to be chosen; the quarter before stays on screen while the next one arrives.
 */
export const brandIntelligenceQuery = (params: BrandIntelligenceParams | undefined) =>
    queryOptions({
        queryKey: reportsKeys.brandIntelligence(params ?? { brandId: 0 }),
        queryFn: params ? () => reportsApi.brandIntelligence(params) : skipToken,
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });

/**
 * App usage for a window and a manager (ALS-160), in one request: the figures, the cards, the
 * people and the folded log load and fail as one. The window before stays on screen while the next
 * one arrives, so a click on the segment control does not blank the tab.
 */
export const appUsageQuery = (params: AppUsageParams) =>
    queryOptions({
        queryKey: reportsKeys.appUsage(params),
        queryFn: () => reportsApi.appUsage(params),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });

/** Every row in App usage's window, fetched when Export is pressed rather than with the tab. */
export const appSessionsWindowQuery = (params: AppUsageParams) =>
    queryOptions({
        queryKey: reportsKeys.appSessionsWindow(params),
        queryFn: () => reportsApi.appSessions.window(params),
        staleTime: 60 * 1000,
    });

/**
 * The Client heatmap in one request (ALS-88): the grids, the call and the log's latest rows load
 * and fail as one. The window before stays on screen while the next one arrives.
 */
export const clientHeatmapQuery = (params: ClientHeatmapParams) =>
    queryOptions({
        queryKey: reportsKeys.clientHeatmap(params),
        queryFn: () => reportsApi.clientHeatmap.report(params),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });

/**
 * Lookbook page attention (ALS-89), apart from the tab's request: its key holds the window and
 * the lookbook but never the manager, so a change of manager cannot refetch or change it. The
 * lookbook before stays on screen while the next one arrives.
 */
export const lookbookAttentionQuery = (params: LookbookAttentionParams) =>
    queryOptions({
        queryKey: reportsKeys.lookbookAttention(params),
        queryFn: () => reportsApi.clientHeatmap.lookbookPages(params),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
    });

/** A page of the heatmap's log, once the register has measured how many rows fit. */
export const clientHeatmapLogQuery = (filters: ActivityLogFilters, enabled = true) =>
    queryOptions({
        queryKey: reportsKeys.clientHeatmapLog(filters),
        queryFn: () => reportsApi.clientHeatmap.log(filters),
        placeholderData: keepPreviousData,
        enabled,
    });
